/* ============================================================
   Set Builder — compose a set, one piece per course.
   State is a map of courseId → chosen item element. The sticky
   summary mirrors the picks (count, slots, subtotal, bundle-unlock
   note, CTA). Adding routes through window.CartAPI; any multi-variant
   pick opens the shared ui-sheet variant picker first. The bundle
   discount itself is a Shopify automatic discount applied at checkout.
   ============================================================ */
(function () {
  /* ---------- helpers ---------- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c];
    });
  }
  function selEsc(v) { return String(v).replace(/["\\]/g, '\\$&'); }

  function cartAdd(items) {
    if (!items || !items.length) return Promise.resolve();
    if (window.CartAPI && typeof window.CartAPI.addItems === 'function') {
      return window.CartAPI.addItems(items);
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) { return r.json(); })
      .then(function () { document.dispatchEvent(new CustomEvent('cart:item-added', { detail: {} })); });
  }

  function deriveOptions(prod) {
    var names = prod.optionNames || [];
    return names.map(function (name, idx) {
      var seen = {}, values = [];
      (prod.variants || []).forEach(function (v) {
        var val = v.options[idx];
        if (val != null && !seen[val]) { seen[val] = 1; values.push(val); }
      });
      return { name: name, values: values, idx: idx };
    });
  }
  function valueAvailable(prod, idx, value, chosen) {
    return prod.variants.some(function (v) {
      if (!v.available) return false;
      if (String(v.options[idx]) !== String(value)) return false;
      for (var k = 0; k < chosen.length; k++) {
        if (k === idx) continue;
        if (chosen[k] != null && String(v.options[k]) !== String(chosen[k])) return false;
      }
      return true;
    });
  }
  function resolveVariant(prod, chosen) {
    if (chosen.some(function (c) { return c == null; })) return null;
    var match = null;
    prod.variants.some(function (v) {
      var ok = chosen.every(function (c, k) { return String(v.options[k]) === String(c); });
      if (ok) { match = v; return true; }
      return false;
    });
    return match;
  }
  function isColorOpt(name) { return /colou?r/i.test(name || ''); }
  function swatchHex(name) {
    try {
      if (window.UntamedColorSwatches && typeof window.UntamedColorSwatches.resolve === 'function') {
        var hex = window.UntamedColorSwatches.resolve(name);
        if (hex) return hex;
      }
    } catch (e) {}
    var n = String(name || '').trim().toLowerCase().replace(/\s+/g, '');
    return /^[a-z]+$/.test(n) ? n : '';
  }

  var CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7"/></svg>';
  var CLOSE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  var LOCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="9" width="16" height="11" rx="1.5"/><path d="M8 9V7a4 4 0 0 1 8 0v2"/></svg>';
  var UNLOCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7"/></svg>';

  function fmtTemplate(str, n) { return String(str || '').replace(/\{n\}/g, n); }

  function init(section) {
    if (section.__sbInit) return;
    section.__sbInit = true;

    var sheetId = section.getAttribute('data-sb-sheet');
    var bundleMin = parseInt(section.getAttribute('data-sb-bundle-min'), 10) || 3;
    var moneyFormat = section.getAttribute('data-sb-money-format') || window.themeMoneyFormat || '${{amount}}';

    var labels = {};
    var labelEl = section.querySelector('[data-sb-labels]');
    if (labelEl) { try { labels = JSON.parse(labelEl.textContent); } catch (e) {} }

    var courses = Array.prototype.slice.call(section.querySelectorAll('[data-sb-course]'));
    var picks = {}; // courseId -> item element

    function money(cents) {
      var c = parseInt(cents, 10) || 0;
      if (typeof window.formatMoney === 'function') {
        try { return window.formatMoney(c, moneyFormat); } catch (e) {}
      }
      return '$' + (c / 100).toFixed(2);
    }

    function itemData(itemEl) {
      var d = { variants: [] };
      var s = itemEl.querySelector('[data-sb-item-data]');
      if (s) { try { d = JSON.parse(s.textContent); } catch (e) {} }
      return d;
    }

    function setItemSel(el, on) {
      el.classList.toggle('sel', on);
      el.setAttribute('aria-pressed', on ? 'true' : 'false');
      var c = el.querySelector('[data-sb-check]');
      if (c) c.hidden = !on;
    }

    function slotEl(cid) { return section.querySelector('[data-sb-slot="' + selEsc(cid) + '"]'); }

    // Finalise a pick: store the chosen variant on the element, then update UI.
    function finalizeSelect(itemEl, variantId, variantTitle) {
      var cid = itemEl.getAttribute('data-course');
      var prev = picks[cid];
      if (prev && prev !== itemEl) { setItemSel(prev, false); prev.__pick = null; }
      itemEl.__pick = { id: variantId, title: variantTitle || '' };
      picks[cid] = itemEl;
      setItemSel(itemEl, true);
      update();
    }

    // Clicking a product: toggle off if already picked, else pick it —
    // opening the variant picker first when the product has options, so the
    // shopper chooses the variant at selection time (not at add-to-bag time).
    function select(itemEl) {
      var cid = itemEl.getAttribute('data-course');
      if (picks[cid] === itemEl) { delete picks[cid]; itemEl.__pick = null; setItemSel(itemEl, false); update(); return; }
      var d = itemData(itemEl);
      var variants = d.variants || [];
      if (variants.length <= 1) { finalizeSelect(itemEl, d.defaultVariantId, ''); return; }
      d.options = deriveOptions(d);
      openPicker(itemEl, d);
    }

    function deselect(cid) {
      var prev = picks[cid];
      if (prev) { setItemSel(prev, false); prev.__pick = null; delete picks[cid]; update(); }
    }

    function renderSlot(cid, pick, required) {
      var slot = slotEl(cid);
      if (!slot) return;
      var coll = slot.getAttribute('data-coll') || '';
      if (pick) {
        var d = itemData(pick);
        var vt = (pick.__pick && pick.__pick.title && pick.__pick.title !== 'Default Title') ? pick.__pick.title : '';
        slot.classList.add('filled');
        slot.innerHTML =
          (d.image ? '<img class="sb__slotfig" src="' + esc(d.image) + '" alt="" loading="lazy">' : '<span class="sb__slotfig"></span>') +
          '<div class="sb__slotmeta"><span class="sb__slotcoll">' + esc(coll) + (vt ? ' · ' + esc(vt) : '') + '</span>' +
          '<span class="sb__slotname">' + esc(d.title) + '</span></div>' +
          '<span class="sb__slotprice mono-num">' + esc(d.priceMoney || money(d.price)) + '</span>' +
          '<button type="button" class="sb__slotx" data-sb-remove="' + esc(cid) + '" aria-label="Remove ' + esc(d.title) + '">' + CLOSE + '</button>';
      } else {
        slot.classList.remove('filled');
        slot.innerHTML =
          '<span class="sb__slotempty" aria-hidden="true"></span>' +
          '<div class="sb__slotmeta"><span class="sb__slotcoll">' + esc(coll) + '</span>' +
          '<span class="sb__slotpick">' + esc(required ? labels.chooseOne : labels.optional) + '</span></div>';
      }
    }

    function update() {
      var count = 0, subtotal = 0, requiredLeft = 0;
      courses.forEach(function (co) {
        var cid = co.getAttribute('data-sb-course');
        var required = co.getAttribute('data-required') === 'true';
        var pick = picks[cid];
        var done = co.querySelector('[data-sb-done]');
        if (done) done.hidden = !pick;
        renderSlot(cid, pick, required);
        if (pick) { count++; subtotal += parseInt(pick.getAttribute('data-price'), 10) || 0; }
        else if (required) { requiredLeft++; }
      });

      var countEl = section.querySelector('[data-sb-count]');
      if (countEl) countEl.textContent = count + ' / ' + courses.length;
      var subEl = section.querySelector('[data-sb-subtotal]');
      if (subEl) subEl.textContent = money(subtotal);

      // bundle unlock note
      var bundle = section.querySelector('[data-sb-bundle]');
      var bundleText = section.querySelector('[data-sb-bundle-text]');
      var bundleIc = section.querySelector('[data-sb-bundle-ic]');
      var unlocked = count >= bundleMin;
      if (bundle) bundle.classList.toggle('on', unlocked);
      if (bundleIc) bundleIc.innerHTML = unlocked ? UNLOCK : LOCK;
      if (bundleText) bundleText.textContent = unlocked
        ? labels.bundleUnlocked
        : fmtTemplate(labels.bundleLocked, Math.max(0, bundleMin - count));

      // CTA
      var cta = section.querySelector('[data-sb-add]');
      if (cta) {
        var ready = requiredLeft === 0 && count > 0;
        cta.classList.toggle('sb__cta--off', !ready);
        cta.disabled = !ready;
        if (ready) {
          var word = count === 1 ? labels.pieceWord : labels.piecesWord;
          cta.textContent = labels.addReady + ' · ' + count + ' ' + word;
        } else if (requiredLeft > 0 && count > 0) {
          cta.textContent = fmtTemplate(labels.addMore, requiredLeft);
        } else {
          cta.textContent = labels.start;
        }
      }

      var clr = section.querySelector('[data-sb-clear]');
      if (clr) clr.disabled = count === 0;
    }

    function clearAll() {
      Object.keys(picks).forEach(function (cid) { var el = picks[cid]; if (el) { setItemSel(el, false); el.__pick = null; } });
      picks = {};
      update();
    }

    function surprise() {
      courses.forEach(function (co) {
        var cid = co.getAttribute('data-sb-course');
        var items = Array.prototype.slice.call(co.querySelectorAll('[data-sb-item]'));
        if (!items.length) return;
        var prev = picks[cid];
        if (prev) { setItemSel(prev, false); prev.__pick = null; }
        var el = items[Math.floor(Math.random() * items.length)];
        var d = itemData(el);
        var vid = d.defaultVariantId, vtitle = '';
        var avail = (d.variants || []).filter(function (v) { return v.available; });
        if (avail.length) { vid = avail[0].id; vtitle = avail[0].title; }
        el.__pick = { id: vid, title: vtitle };
        picks[cid] = el;
        setItemSel(el, true);
      });
      update();
    }

    /* ---------- add to bag — variants already chosen, no prompt ---------- */
    function addSet() {
      var items = [];
      courses.forEach(function (co) {
        var el = picks[co.getAttribute('data-sb-course')];
        if (el && el.__pick && el.__pick.id) items.push({ id: el.__pick.id, quantity: 1 });
      });
      if (!items.length) return;
      var cta = section.querySelector('[data-sb-add]');
      if (cta) cta.disabled = true;
      cartAdd(items).then(function () { if (cta) cta.disabled = false; })
        .catch(function () { if (cta) cta.disabled = false; });
    }

    /* ---------- variant picker (opens on product click, single product) ---------- */
    function openPicker(itemEl, p) {
      var VP = (window.UntamedSheets && window.UntamedSheets.vp) || {};
      var sheetEl = sheetId ? document.querySelector('[data-ui-sheet="' + selEsc(sheetId) + '"]') : null;
      var body = sheetEl ? sheetEl.querySelector('[data-sb-sheet-body]') : null;
      // graceful fallback: no sheet component -> select the default variant
      if (!body || !window.UISheet) { finalizeSelect(itemEl, p.defaultVariantId, ''); return; }

      var chosen = p.options.map(function (o) { return o.values.length === 1 ? o.values[0] : null; });
      function ready() { return !!resolveVariant(p, chosen); }

      function draw() {
        body.innerHTML = window.UntamedSheets.renderVariantPicker([{
          pi: 0, title: p.title, image: p.image,
          options: p.options.map(function (o) {
            var color = isColorOpt(o.name);
            return {
              name: o.name, oi: o.idx, isColor: color,
              selected: color ? chosen[o.idx] : null,
              values: o.values.map(function (val) {
                return { value: val, selected: String(chosen[o.idx]) === String(val), available: valueAvailable(p, o.idx, val, chosen), hex: color ? swatchHex(val) : null };
              })
            };
          })
        }], {
          intro: VP.intro || 'Pick your options, then it’s added to your set.',
          addAttr: 'data-sb-sheet-add',
          addLabel: VP.setLabel || 'Add to set',
          selectLabel: VP.select || 'Select options',
          ready: ready()
        });
        body.querySelectorAll('[data-vp-val]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            if (btn.getAttribute('aria-disabled') === 'true') return;
            var oi = parseInt(btn.getAttribute('data-vp-oi'), 10);
            var val = btn.getAttribute('data-vp-val');
            chosen[oi] = String(chosen[oi]) === String(val) ? null : val;
            draw();
          });
        });
        var add = body.querySelector('[data-sb-sheet-add]');
        if (add) add.addEventListener('click', function () {
          var v = resolveVariant(p, chosen);
          if (!v) return;
          finalizeSelect(itemEl, v.id, v.title);
          window.UISheet.close();
        });
      }

      draw();
      window.UISheet.open(sheetId, { title: (labels.sheetTitle || 'Choose your options') });
    }

    /* ---------- events ---------- */
    section.addEventListener('click', function (e) {
      var item = e.target.closest('[data-sb-item]');
      if (item && section.contains(item)) { e.preventDefault(); select(item); return; }
      var rm = e.target.closest('[data-sb-remove]');
      if (rm) { e.preventDefault(); deselect(rm.getAttribute('data-sb-remove')); return; }
      if (e.target.closest('[data-sb-surprise]')) { e.preventDefault(); surprise(); return; }
      if (e.target.closest('[data-sb-clear]')) { e.preventDefault(); clearAll(); return; }
      if (e.target.closest('[data-sb-add]')) { e.preventDefault(); addSet(); return; }
    });

    update();
  }

  function boot() { document.querySelectorAll('[data-sb]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
